import React, { memo, useContext } from 'react';
import { connect } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';

import InstagramIcon from 'mdi-react/InstagramIcon';
import FacebookIcon from 'mdi-react/FacebookBoxIcon';
import TwitterIcon from 'mdi-react/TwitterBoxIcon';

import { AppState } from '../../reducers';
import Viewport from '../../components/viewport';
import ChangeMarket from './components/change-market';

import { Site, SocialNetwork, Url } from './reducers/types';
import { Column } from './types';
import { Page, Pages } from '../../globals/actions/types';

import { SIDEBAR_STATE } from '../sidebar/constants';

import StaticIntercom from '../../components/static-intercom';

import * as S from './styles';

const ocastLink = {
  no: 'https://ocast.com/nb-no/brands/prisjakt-no-4705',
  se: 'https://ocast.com/sv-se/brands/prisjakt-nu-147',
};

const careersLink = {
  se: 'https://prisjakt.teamtailor.com/',
};

type StateProps = {
  socialNetworks: SocialNetwork[];
  current: string;
  sites: Site[];
  pages?: Pages;
  url?: Url;
  sidebarOpen: boolean;
};

type OwnProps = {};

type Props = OwnProps & StateProps;

const Footer = ({
  socialNetworks,
  current,
  sites,
  pages,
  url,
  sidebarOpen,
}: Props) => {
  const { t, market, linkTo } = useContext(AppContext);

  const infoPageColumns = () => {
    const { infoPageMap } = market;
    const infoPages = pages
      ? ([
          ...pages.nodes,
          url &&
            url.campaigns && {
              pathName: url.campaigns,
              title: t('campaigns', 'themes.title'),
              id: 'themes',
            },
          ocastLink[market.code] && {
            pathName: ocastLink[market.code],
            title: t('footer', 'link.ads'),
            id: 'adlink',
          },
          careersLink[market.code] && {
            pathName: careersLink[market.code],
            title: t('footer', 'link.careers'),
            id: 'careers',
          },
          infoPageMap?.articles && {
            pathName: linkTo('editorialContentListing'),
            title: t('common', 'article.articles'),
            id: 'articles',
          },
        ].filter(page => page) as Page[])
      : ([] as Page[]);

    const sortColumns = infoPageMap
      ? [
          {
            title: t('footer', 'pages.title'),
            pages: [
              infoPageMap.about,
              infoPageMap.priceInformation,
              infoPageMap.aboutReviews,
              infoPageMap.contact,
              infoPageMap.registerShop,
              infoPageMap.adlink,
            ],
          },
          {
            title: t('footer', 'pages.rules-title'),
            pages: [
              infoPageMap.userAgreement,
              infoPageMap.disclaimer,
              infoPageMap.privacyPolicy,
              infoPageMap.cookiePolicy,
            ],
          },
          {
            title: t('footer', 'pages.on-title'),
            pages: [
              infoPageMap.careers,
              infoPageMap.themes,
              infoPageMap.media,
              infoPageMap.articles,
            ],
          },
        ]
      : [];

    return sortColumns.map(sortColumn => {
      const columnPages = [] as Page[];
      infoPages.forEach(page => {
        if (sortColumn.pages.includes(page.id)) {
          columnPages.push(page);
        }
      });
      columnPages.sort(
        (a, b) =>
          sortColumn.pages.indexOf(a.id) - sortColumn.pages.indexOf(b.id)
      );
      return { title: sortColumn.title, pages: columnPages } as Column;
    });
  };

  return (
    <S.Footer sidebarOpen={sidebarOpen}>
      <S.TopSection>
        <S.Content sidebarOpen={sidebarOpen}>
          <StaticIntercom
            customButton={
              <S.FeedbackButton
                title={t('footer', 'link.contact')}
                data-test="FeedbackButton"
                color="primary"
              >
                <S.ButtonText>{t('footer', 'link.contact')}</S.ButtonText>
              </S.FeedbackButton>
            }
          />

          <S.Sites>
            <ChangeMarket sites={sites} current={current} />
          </S.Sites>
        </S.Content>
      </S.TopSection>

      <S.MidSection>
        <S.Content sidebarOpen={sidebarOpen}>
          <S.NewsletterSignup
            title={t('footer', 'newsletter.title')}
            description={t('footer', 'newsletter.sub-title')}
            stacked
            variant="secondary"
          />
          {pages && (
            <S.InfoPages
              infoPageColumns={infoPageColumns()}
              numberOfColumns={2}
            />
          )}
          <S.Download>
            <S.ColumnTitle>{t('footer', 'mobile-apps.title')}</S.ColumnTitle>{' '}
            <S.DownloadIcons
              dangerouslySetInnerHTML={{
                __html: t('footer', 'download.text.md'),
              }}
            />
          </S.Download>
        </S.Content>
      </S.MidSection>
      <S.BottomSection>
        <S.Content sidebarOpen={sidebarOpen}>
          <S.LogoWrapper>
            <S.Logo
              styling="light"
              market={market.code}
              companyName={t('meta', 'site.name')}
            />
            <Viewport mobile>
              <div>
                <p>
                  {t('footer', 'info.copyright', {
                    year: new Date().getFullYear(),
                  })}
                </p>
              </div>
            </Viewport>
            <div>
              {socialNetworks.map(({ name, link }) => (
                <S.SocialIcon
                  key={name}
                  title={name}
                  href={link}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                >
                  {name === 'instagram' && <InstagramIcon size={40} />}
                  {name === 'facebook' && <FacebookIcon size={40} />}
                  {name === 'twitter' && <TwitterIcon size={40} />}
                </S.SocialIcon>
              ))}
            </div>
          </S.LogoWrapper>
          <Viewport tablet desktop>
            <div>
              <p>
                {t('footer', 'info.copyright', {
                  year: new Date().getFullYear(),
                })}
              </p>
            </div>
          </Viewport>
        </S.Content>
      </S.BottomSection>
    </S.Footer>
  );
};

const mapStateToProps = (state: AppState) => ({
  socialNetworks: state.footer.socialNetworks,
  current: state.footer.current,
  sites: state.footer.sites,
  pages: state.globals.infoPages,
  url: state.footer.url,
  sidebarOpen: state.areas.sidebar.tab !== SIDEBAR_STATE.NONE,
});

export default connect(mapStateToProps, {})(memo(Footer));
